import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getStartup, getStartupRadar, addToWatchlist, removeFromWatchlist, checkWatchlist, sendContact, getStoredUser, getBodaccForStartup } from '../api';
import { ScoreGauge, RiskBadge } from '../components/ScoreGauge';
import RadarChart from '../components/RadarChart';
import { LogoImg, getDomain } from '../components/StartupCard';

const CLUSTER_LABELS = [
  { key: 'cl01_bp', label: 'Business Plan' }, { key: 'cl02_ratio', label: 'Ratio Efficacité Commerciale' },
  { key: 'cl03_solidite_fi', label: 'Solidité Financière' }, { key: 'cl04_maturite_fi', label: 'Maturité Financière' },
  { key: 'cl05_marche', label: 'Marché' }, { key: 'cl06_innovation', label: 'Innovation' },
  { key: 'cl07_equipe', label: 'Équipe Dirigeante' }, { key: 'cl08_org_comm', label: 'Organisation Commerciale' },
  { key: 'cl09_notoriete', label: 'Notoriété' }, { key: 'cl10_rse', label: 'RSE' },
  { key: 'cl11_rgpd', label: 'RGPD / Sécurité' }, { key: 'cl12_conformite', label: 'Conformité / Contrôle' },
];

export default function StartupDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [startup, setStartup] = useState(null);
  const [radar, setRadar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inWatchlist, setInWatchlist] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [contactMsg, setContactMsg] = useState('');
  const [contactSent, setContactSent] = useState(false);
  const [bodacc, setBodacc] = useState([]);
  const [openSections, setOpenSections] = useState({ presentation: true, bodacc: true, infos: true });
  const toggle = (key) => setOpenSections(s => ({ ...s, [key]: !s[key] }));
  const user = getStoredUser();
  const isInvestor = user?.role === 'investisseur';

  useEffect(() => {
    Promise.all([getStartup(id), getStartupRadar(id).catch(() => null)])
      .then(([s, r]) => { setStartup(s); setRadar(r); })
      .catch(console.error)
      .finally(() => setLoading(false));
    if (isInvestor) checkWatchlist(id).then(r => setInWatchlist(r.inWatchlist)).catch(() => {});
    getBodaccForStartup(id).then(setBodacc).catch(() => {});
  }, [id, isInvestor]);

  const handleWatchlist = async () => {
    if (inWatchlist) await removeFromWatchlist(id);
    else await addToWatchlist(id);
    setInWatchlist(!inWatchlist);
  };

  const handleContact = async () => {
    await sendContact({ startup_id: parseInt(id), message: contactMsg });
    setContactSent(true);
    setShowContact(false);
  };

  if (loading) return <div className="loading">Chargement...</div>;
  if (!startup) return <div className="loading">Startup introuvable</div>;

  const noteS = parseFloat(startup.note_sectorielle);
  const riskClass = noteS > 7 ? 'vert' : noteS >= 4 ? 'orange' : 'rouge';

  return (
    <div>
      <div className="detail-header">
        <div className="container">
          <button onClick={() => navigate(-1)}
            style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, marginBottom: 16, display: 'inline-block',
              background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
            &larr; Retour
          </button>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <LogoImg
                src={startup.logo_url}
                name={startup.startup_name || startup.name}
                size={64}
              />
              <div>
                <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 8 }}>{startup.startup_name || startup.name}</h1>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <RiskBadge level={startup.risk_level} />
                  {startup.categorie && <span className="badge badge-secteur">{startup.categorie}</span>}
                  {startup.sous_domaine && <span className="badge" style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }}>{startup.sous_domaine}</span>}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {/* Action buttons - top right */}
              {isInvestor && (
                <>
                  <button onClick={() => setShowContact(true)} className="btn" disabled={contactSent}
                    style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.3)', fontSize: 13, padding: '8px 16px' }}>
                    {contactSent ? 'Demande envoyée' : 'Contacter'}
                  </button>
                  <button onClick={handleWatchlist} className="btn"
                    style={{ background: inWatchlist ? '#39A8E5' : 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.3)', fontSize: 13, padding: '8px 16px' }}>
                    {inWatchlist ? '★ Dans les favoris' : '☆ Favoris'}
                  </button>
                </>
              )}
              {!user && (
                <Link to="/login" className="btn" style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.3)', fontSize: 13, padding: '8px 16px', textDecoration: 'none' }}>
                  Se connecter
                </Link>
              )}
              <ScoreGauge score={startup.note_sectorielle} size="lg" />
            </div>
          </div>
        </div>
      </div>

      <div className="container" style={{ padding: '32px 24px' }}>
        {/* Présentation - between header and metrics */}
        {startup.pitch && (
          <div className="card" style={{ marginBottom: 24 }}>
            <div onClick={() => toggle('presentation')} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}>
              <h3 style={{ margin: 0 }}>Présentation</h3>
              <span style={{ fontSize: 20, color: 'var(--text-light)', transition: 'transform 0.2s', transform: openSections.presentation ? 'rotate(0deg)' : 'rotate(-90deg)' }}>&#9662;</span>
            </div>
            {openSections.presentation && (
              <p style={{ lineHeight: 1.8, color: 'var(--text-light)', fontSize: 14, marginTop: 12 }}>{startup.pitch}</p>
            )}
          </div>
        )}

        {/* Info cards / Metrics */}
        <div className="stats-grid" style={{ marginBottom: 32 }}>
          {[['Maturité', startup.maturite], ['Stade', startup.stade_dev], ["Chiffre d'affaires", startup.ca],
            ['Effectif', startup.effectif], ['Clientèle', startup.adressage_client],
            ['Round', startup.round_levee ? `Round ${startup.round_levee}` : null],
            ['Cohérence', startup.taux_coherence ? `${parseFloat(startup.taux_coherence).toFixed(0)}%` : null],
            ['Région', startup.region]
          ].filter(([_, v]) => v).map(([label, val]) => (
            <div key={label} className="stat-card">
              <div className="stat-value" style={{ fontSize: 20 }}>{val || '-'}</div>
              <div className="stat-label">{label}</div>
            </div>
          ))}
        </div>

        {/* Notes row */}
        {startup.note_sectorielle && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>Scores ISI</h3>
            {startup.score_year && (
              <span className="badge" style={{ background: '#E3F2FD', color: '#1565C0', fontSize: 11 }}>
                Campagne {startup.score_year}
              </span>
            )}
          </div>
        )}
        <div style={{ display: 'flex', gap: 16, marginBottom: 32, flexWrap: 'wrap' }}>
          <div className="card" style={{ flex: 1, minWidth: 200, textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 8 }}>Note Pivot</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--primary)' }}>
              {startup.note_pivot ? parseFloat(startup.note_pivot).toFixed(2) : '-'}
            </div>
          </div>
          <div className="card" style={{ flex: 1, minWidth: 200, textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 8 }}>Note Sectorielle ISI</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: riskClass === 'vert' ? '#27AE60' : riskClass === 'orange' ? '#F39C12' : '#E74C3C' }}>
              {noteS ? noteS.toFixed(2) : '-'}
            </div>
          </div>
          <div className="card" style={{ flex: 1, minWidth: 200, textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 8 }}>Top Force</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#27AE60' }}>{startup.top_force || '-'}</div>
          </div>
          <div className="card" style={{ flex: 1, minWidth: 200, textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 8 }}>Top Faiblesse</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#E74C3C' }}>{startup.top_faiblesse || '-'}</div>
          </div>
        </div>

        <div className="detail-grid">
          <div className="card">
            <h3 style={{ marginBottom: 16 }}>Radar de Résilience</h3>
            {radar ? (
              <RadarChart labels={radar.labels} startupData={radar.startup} sectorAvg={radar.sector_avg}
                startupName={startup.startup_name || startup.name} height={400} />
            ) : <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-light)' }}>Données non disponibles</div>}
          </div>

          <div className="card">
            <h3 style={{ marginBottom: 16 }}>Notes par Cluster</h3>
            <table className="cluster-table">
              <thead><tr><th>Cluster</th><th style={{ textAlign: 'center' }}>Note /10</th><th style={{ textAlign: 'center' }}>Statut</th></tr></thead>
              <tbody>
                {CLUSTER_LABELS.map(({ key, label }) => {
                  const score = parseFloat(startup[key]);
                  const color = score > 7 ? '#27AE60' : score >= 4 ? '#F39C12' : '#E74C3C';
                  return (
                    <tr key={key}>
                      <td style={{ fontWeight: 500 }}>{label}</td>
                      <td style={{ textAlign: 'center', fontWeight: 700, color }}>{score ? score.toFixed(1) : '-'}</td>
                      <td style={{ textAlign: 'center' }}>
                        {score ? <span className={`badge badge-${score > 7 ? 'vert' : score >= 4 ? 'orange' : 'rouge'}`}>
                          {score > 7 ? 'Bon' : score >= 4 ? 'Vigilance' : 'Critique'}</span> : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* BODACC - collapsible, above Informations détaillées */}
        <div className="card" style={{ marginTop: 24, borderLeft: bodacc.length > 0 ? '4px solid #2E75B6' : '4px solid #E0E0E0' }}>
          <div onClick={() => toggle('bodacc')} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}>
            <h3 style={{ margin: 0 }}>
              Historique BODACC
              <span className="badge" style={{ marginLeft: 8, fontSize: 10,
                background: bodacc.length > 0 ? '#E3F2FD' : '#F5F5F5',
                color: bodacc.length > 0 ? '#1565C0' : '#999' }}>
                {bodacc.length > 0 ? `${bodacc.length} annonce${bodacc.length > 1 ? 's' : ''}` : 'Aucune annonce'}
              </span>
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {bodacc.length > 0 && (
                <span style={{ fontSize: 11, color: 'var(--text-light)' }}>
                  MAJ : {new Date(bodacc[0].fetched_at).toLocaleDateString('fr-FR')}
                </span>
              )}
              <span style={{ fontSize: 20, color: 'var(--text-light)', transition: 'transform 0.2s', transform: openSections.bodacc ? 'rotate(0deg)' : 'rotate(-90deg)' }}>&#9662;</span>
            </div>
          </div>
          {openSections.bodacc && (
            <div style={{ marginTop: 12 }}>
              {bodacc.length > 0 ? (
                <div style={{ display: 'grid', gap: 8 }}>
                  {bodacc.map((b, i) => (
                    <div key={i} style={{ display: 'flex', gap: 12, padding: '10px 12px', background: 'var(--bg-secondary)', borderRadius: 8, fontSize: 13 }}>
                      <div style={{ minWidth: 85, color: 'var(--text-light)', fontWeight: 500 }}>
                        {b.date_parution ? new Date(b.date_parution).toLocaleDateString('fr-FR') : '-'}
                      </div>
                      <div>
                        <span className="badge" style={{ fontSize: 10, marginRight: 8,
                          background: b.famille_avis?.includes('Création') ? '#E8F5E9' : b.famille_avis?.includes('Modification') ? '#FFF3E0' : b.famille_avis?.includes('Radiation') ? '#FFEBEE' : '#F5F5F5',
                          color: b.famille_avis?.includes('Création') ? '#2E7D32' : b.famille_avis?.includes('Modification') ? '#E65100' : b.famille_avis?.includes('Radiation') ? '#C62828' : '#666' }}>
                          {b.famille_avis || 'Annonce'}
                        </span>
                        <span style={{ color: 'var(--text-light)' }}>{b.commercant}</span>
                        {b.tribunal && <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{b.tribunal}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: 'var(--text-light)', fontSize: 13, margin: 0 }}>
                  Aucune annonce BODACC trouvée. Les données sont mises à jour par l'administrateur.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Informations détaillées - collapsible */}
        {(startup.fondateurs || startup.investisseurs_details || startup.levees_details || startup.site_web || startup.adresse || startup.siret || startup.siren) && (
          <div className="card" style={{ marginTop: 24 }}>
            <div onClick={() => toggle('infos')} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}>
              <h3 style={{ margin: 0 }}>Informations détaillées</h3>
              <span style={{ fontSize: 20, color: 'var(--text-light)', transition: 'transform 0.2s', transform: openSections.infos ? 'rotate(0deg)' : 'rotate(-90deg)' }}>&#9662;</span>
            </div>
            {!openSections.infos ? null : <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, fontSize: 14, marginTop: 16 }}>
              {startup.fondateurs && (
                <div><span style={{ fontWeight: 600, color: 'var(--text-light)', fontSize: 12, textTransform: 'uppercase' }}>Fondateur(s)</span><div style={{ marginTop: 4 }}>{startup.fondateurs}</div></div>
              )}
              {startup.annee_creation && (
                <div><span style={{ fontWeight: 600, color: 'var(--text-light)', fontSize: 12, textTransform: 'uppercase' }}>Année de création</span><div style={{ marginTop: 4 }}>{startup.annee_creation}</div></div>
              )}
              {startup.investisseurs_details && (
                <div><span style={{ fontWeight: 600, color: 'var(--text-light)', fontSize: 12, textTransform: 'uppercase' }}>Investisseur(s)</span><div style={{ marginTop: 4 }}>{startup.investisseurs_details}</div></div>
              )}
              {startup.levees_details && (
                <div><span style={{ fontWeight: 600, color: 'var(--text-light)', fontSize: 12, textTransform: 'uppercase' }}>Levée(s) de fonds</span><div style={{ marginTop: 4 }}>{startup.levees_details}</div></div>
              )}
              {(startup.siret || startup.siren) && (
                <div><span style={{ fontWeight: 600, color: 'var(--text-light)', fontSize: 12, textTransform: 'uppercase' }}>SIRET / SIREN</span><div style={{ marginTop: 4, fontFamily: 'monospace', letterSpacing: 1 }}>{startup.siret || startup.siren}</div></div>
              )}
              {startup.immatriculation && (
                <div><span style={{ fontWeight: 600, color: 'var(--text-light)', fontSize: 12, textTransform: 'uppercase' }}>Immatriculation / Agrément</span><div style={{ marginTop: 4 }}>{startup.immatriculation}</div></div>
              )}
              {startup.site_web && (
                <div><span style={{ fontWeight: 600, color: 'var(--text-light)', fontSize: 12, textTransform: 'uppercase' }}>Site web</span><div style={{ marginTop: 4 }}><a href={startup.site_web} target="_blank" rel="noopener noreferrer">{startup.site_web}</a></div></div>
              )}
              {startup.linkedin && (
                <div><span style={{ fontWeight: 600, color: 'var(--text-light)', fontSize: 12, textTransform: 'uppercase' }}>LinkedIn</span><div style={{ marginTop: 4 }}><a href={startup.linkedin} target="_blank" rel="noopener noreferrer">Voir le profil</a></div></div>
              )}
              {startup.adresse && (
                <div><span style={{ fontWeight: 600, color: 'var(--text-light)', fontSize: 12, textTransform: 'uppercase' }}>Adresse</span><div style={{ marginTop: 4 }}>{startup.adresse}</div></div>
              )}
              {startup.labels_prix && (
                <div style={{ gridColumn: '1 / -1' }}><span style={{ fontWeight: 600, color: 'var(--text-light)', fontSize: 12, textTransform: 'uppercase' }}>Labels et prix</span><div style={{ marginTop: 4 }}>{startup.labels_prix}</div></div>
              )}
            </div>}
          </div>
        )}

        {/* Action buttons moved to header top-right */}

        {/* Contact modal */}
        {showContact && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 1000 }}>
            <div className="card" style={{ width: 480, maxWidth: '90%' }}>
              <h3 style={{ marginBottom: 16 }}>Contacter {startup.startup_name || startup.name}</h3>
              <textarea value={contactMsg} onChange={e => setContactMsg(e.target.value)}
                className="form-input" rows={4} placeholder="Votre message..."
                style={{ resize: 'vertical', marginBottom: 16 }} />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setShowContact(false)} className="btn btn-outline">Annuler</button>
                <button onClick={handleContact} className="btn btn-accent" disabled={!contactMsg.trim()}>Envoyer</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
